import React, { useState , useEffect } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function CyclosporinCalculator({ user }) {
    // Logic inside your component
    const [weight, setWeight] = useState('0');
    const [age, setAge] = useState('0');
    const [isLiverFailure, setIsLiverFailure] = useState(false);
    const [route, setRoute] = useState('oral');
    const [indication, setIndication] = useState("Kidney Transplant");
    const [monthsPost, setMonthsPost] = useState("0-6"); // 0-6, 7-12, >12
    const [risk, setRisk] = useState("Moderate"); // Low, Moderate, High
    const [liverMonths, setLiverMonths] = useState(">3 months");

    // State for the user inputs
    const [currentDose, setCurrentDose] = useState('0');
    const [measuredLevel, setMeasuredLevel] = useState('0');
    const [levelType, setLevelType] = useState('C0'); // 'C0' or 'C2'
    const [interval, setInterval] = useState('');
    const [simDose, setSimDose] = useState('');
    


    // Formula: Desired Dose = (Css Desired / Css Current) * Current Dose
    const getClPopulation = () => {
    let cl_ml_kg_min = 7.5; // Default Adult Oral Mean
        if (isLiverFailure) {
            cl_ml_kg_min = 3.0;
        } else if (age < 12) {
            cl_ml_kg_min = 12.5; // Pediatric Mean (10-15 range)
        } else if (route === 'iv') {
            cl_ml_kg_min = 6.0; // IV Adult Mean (5-7 range)
        }
        // Applying your handbook formula: CL = ml/kg/min * weight * 0.06
        return cl_ml_kg_min * weight * 0.06;
    };
    const currentFactor = isLiverFailure ? "3.0" : (age < 12 ? "12.5" : (route === 'iv' ? "6.0" : "7.5"));
    const factorLabel = isLiverFailure 
    ? "(Liver Failure factor)" 
    : (age < 12
        ? "(Pediatric factor)" 
        : (route === 'iv' ? "(IV Adult factor)" : "(Oral Adult factor)"));
    const clPopulation = getClPopulation();
    const indicationTargets = {
        "Kidney Transplant": { type: "transplant" },
        "Liver Transplant": { type: "liver" },
        "Bone Marrow Transplant": { type: "bone"},
        "Others transplant (heart, lungs)": { type: "others" },
        "Ulcerative colitis": { type: "fixed", range: "150 - 350" },
        "Aplastic anemia": { type: "fixed", range: "75 - 200" },
        "Nephrotic syndrome": { type: "fixed", range: "125 - 250" },
        "Lupus nephritis": { type: "fixed", range: "75 - 200" },
        "Idiopathic thombocytic purpura": { type:"fixed", range:"200-400" },
        "Hypoplastic myelodyplast syndrome": { type: "fixed", range: "100-300"},
        "Langerhans histocytosis": {type: "fixed", range: "300-400"},
        "Hemophagocytic lymphohistiocytosis": {type: "fixed", range: "150-250"},
        "Myasthenia gravis": { type: "fixed", range: "300 - 500" },
        "Graft vs host disease": { type: "fixed", range: "200 - 600" }
    };
    useEffect(() => {
        if (levelType === 'C2') {
            if (indication !== "Kidney Transplant" && indication !== "Liver Transplant") {
                setIndication("Kidney Transplant");
            }
        }
    }, [levelType, indication]); // Include both dependencies
    const getTargetRange = () => {
        // BLOCK 1: C2 Peak Logic
        if (levelType === 'C2') {
            if (indication === "Kidney Transplant") {
                if (monthsPost === "0-1") return "1700";
                if (monthsPost === "2") return "1500";
                if (monthsPost === "3") return "1300";
                if (monthsPost === "4-6") return "900 - 1000";
                if (monthsPost === "7-12") return "700 - 900";
                return "700 - 800"; // >12 months
            }
            
            if (indication === "Liver Transplant") {
                if (liverMonths === "0-3m") return "1000";
                if (liverMonths === "4-6m") return "800";
                return "600"; // >6 months
            }
            
            return "N/A for this indication"; // C2 is only for Kidney and Liver
        } 

        // BLOCK 2: C0 Trough Logic
        else {
            if (indication === "Kidney Transplant") {
                if (age >= 12) {
                    if (monthsPost === "0-6") return risk === "Low" ? "150-275" : risk === "High" ? "200-350" : "175-325";
                    if (monthsPost === "7-12") return risk === "Low" ? "100-200" : risk === "High" ? "150-250" : "125-225";
                    return risk === "Low" ? "50-150" : risk === "High" ? "100-200" : "75-175";
                } else {
                    return monthsPost === "<3 months" ? "300 - 400" : "100 - 300";
                }
            }

            if (indication === "Liver Transplant") {
                if (age < 12) {
                    // Pediatric targets
                    return liverMonths === "<3 months" ? "200 - 250" : "100 - 125"; 
                }
                return "200 - 500"; // Adult Liver target
            }

            if (indication === "Bone Marrow Transplant") {
                return age < 12 ? "100 - 250" : "250 - 500";
            }

            if (indication === "Others transplant (heart, lungs)") {
                return age < 12 ? "100 - 400" : "300 - 500";
            }
            
            // Final fallback for indications from your fixed list (Aplastic Anemia, etc.)
            const data = indicationTargets[indication];
            return data?.range || "Consult Handbook";
        }
    };
             
    
    // 1. Get the "Raw" dose based on the active button
    // Step 3 Logic
    const F = 0.3; // 30% for Oral
    const S = 1.0;
    

    // Step 3 Math logic
    const predictedCss = clPopulation > 0 && interval >0 ? (Number(currentDose) * F * S * 1000) / (clPopulation * Number(interval)):0;
    // Logic to check if it's within target
    const checkRangeStatus = () => {
        const rangeStr = getTargetRange(); // This gets your "100-300"
        
        // 1. If it's a range like "100-300"
        if (rangeStr.includes("-")) {
            const parts = rangeStr.split("-").map(s => parseFloat(s.trim()));
            const min = parts[0];
            const max = parts[1];

            if (predictedCss < min) return { label: "TOO LOW", color: "#3498db" };
            if (predictedCss > max) return { label: "TOO HIGH", color: "#e74c3c" };
            return { label: "WITHIN TARGET", color: "#2ecc71" };
        }

        // 2. If it's a single value like "1700" (C2 targets often are)
        const singleTarget = parseFloat(rangeStr);
        if (!isNaN(singleTarget)) {
            if (predictedCss < singleTarget * 0.9) return { label: "TOO LOW", color: "#3498db" };
            if (predictedCss > singleTarget * 1.1) return { label: "TOO HIGH", color: "#e74c3c" };
            return { label: "NEAR TARGET", color: "#2ecc71" };
        }

        return { label: "CHECK RANGE", color: "#95a5a6" };
    };

    const status = (predictedCss > 0) ? checkRangeStatus() : { label: "WAITING", color: "#95a5a6" };

    // Step 4 Logic
    const actualLevel = parseFloat(measuredLevel) || 0;
    // 1. Calculate Deviation (%)
    const levelDeviation = (predictedCss > 0 && actualLevel > 0) 
        ? ((actualLevel - predictedCss) / predictedCss) * 100 
        : 0;   
    // 2. Calculate Individual Clearance (L/hr)
    // Formula: CL = (Dose * F * S * 1000) / (ActualLevel * Interval)
    const individualCL = (actualLevel > 0) 
        ? (Number(currentDose) * 0.3 * 1.0 * 1000) / (actualLevel * 12) 
        : 0;
    const getMetabolismStatus = () => {
        if (levelDeviation > 25) return { text: "Slow Metabolizer", color: "#e67e22", note: `Level is ${levelDeviation.toFixed(1)}% higher than predicted. Check for drug interactions (CYP3A4 inhibitors).` };
        if (levelDeviation < -25) return { text: "Fast Metabolizer", color: "#3498db", note: `"Level is ${levelDeviation.toFixed(1)}% lower than predicted. Check for non-compliance, induction or malabsorption.` };
        return { text: "Normal Metabolizer", color: "#2ecc71", note: "Patient follows population average." };
    };

    const metabolism = getMetabolismStatus();

    // === RE-ENGINEERED DOSE SIMULATION CORE (INDIVIDUAL HIGH-PRECISION) ===
    const simDoseNum = parseFloat(simDose) || 0;
    const currentDoseNum = parseFloat(currentDose) || 0;
    const intervalNum = parseFloat(interval) || 12; // Standard default fallback to 12h if empty
    const measuredVal = parseFloat(measuredLevel) || 0;
    
    let simResult = 0;

    if (simDoseNum > 0 && currentDoseNum > 0 && measuredVal > 0) {
        // Direct individual patient ratio scaling matching the real biology footprint
        simResult = (simDoseNum / currentDoseNum) * measuredVal;
    } else if (simDoseNum > 0 && clPopulation > 0 && intervalNum > 0) {
        // Fallback to population constants ONLY if no measured lab baseline exists yet
        simResult = (simDoseNum * 0.3 * 1.0 * 1000) / (clPopulation * intervalNum);
    }

    const checkSimTarget = (val) => {
        const rangeStr = getTargetRange();
        if (!rangeStr || rangeStr === "Consult Handbook" || rangeStr.includes("N/A")) {
            return { label: "NO TARGET", color: "#95a5a6" };
        }

        // 1. If it's a range like "100-300"
        if (rangeStr.includes("-")) {
            const parts = rangeStr.split("-").map(s => parseFloat(s.trim()));
            const min = parts[0];
            const max = parts[1];

            if (val < min) return { label: "BELOW TARGET", color: "#3498db" };
            if (val > max) return { label: "ABOVE TARGET", color: "#e74c3c" };
            return { label: "WITHIN TARGET", color: "#2ecc71" };
        }

        // 2. If it's a single value like "1700" (C2 targets)
        const singleTarget = parseFloat(rangeStr);
        if (!isNaN(singleTarget)) {
            if (val < singleTarget * 0.9) return { label: "BELOW TARGET", color: "#3498db" };
            if (val > singleTarget * 1.1) return { label: "ABOVE TARGET", color: "#e74c3c" };
            return { label: "AT TARGET", color: "#2ecc71" };
        }

        return { label: "CHECK RANGE", color: "#95a5a6" };
    };
    // Check if this simulated result hits the target range
    const simStatus = (simResult > 0) ? checkSimTarget(simResult) : { label: "", color: "" };
    // Interpretation Logic
    const measuredStatus = (measuredVal > 0) ? checkSimTarget(measuredVal) : { label: "PENDING", color: "#95a5a6" };

    let interpretation = "";
    let recommendation = "";

    if (measuredVal > 0) {
        if (measuredStatus.label === "WITHIN TARGET" || measuredStatus.label === "AT TARGET") {
            interpretation = "The current level is optimal for the chosen indication.";
            recommendation = "If response is poor but compliance good, consider a small dose increment (do not exceed max). If response is good, continue current dose.";
        } else if (measuredStatus.label === "BELOW TARGET") {
            interpretation = "Level is sub-therapeutic for the chosen indication. If response is poor, Check compliance first before consider a dose increment. If response is good, continue current dose.";
            recommendation = "Review patient compliance and potential drug enzyme inducers (e.g., Antibiotics: Rifampicin, Imipenem ; Antifungal: Caspofungin, Terbinafine, Griseofulvin ; Anticonvulsants: Carbamazepine, Phenobarbital, Phenytoin ; Others: Orlistat, St. John’s Wart, Other CYP3A4 inducers).";
        } else if (measuredStatus.label === "ABOVE TARGET") {
            interpretation = "Level is supratherapeutic for the chosen indication. High risk of nephrotoxicity, neurotoxicity, and hypertension.";
            recommendation = "Withhold or reduce dose immediately. Monitor Serum Creatinine closely. Check for CYP3A4 inhibitors (e.g., Calcium channel blockers: Diltiazem, Verapamil ; Antibiotics: Clarithromycin, Erythromycin, Metronidazole ; HIV protease inhibitors: Indinavir, Ritonavir ; Gastrointestinal prokinetic agents: Metoclopramide ; Immunosuppresants: Sirolimus, Tacrolimus ; Antifungal agents: Flucanazole, Itraconazole, Ketoconazole, Voriconazole ; Others: Bromocriptine, Cimetidine, Ethinyl estradiol, Methylprednisolone, Grapefruit juice, NSAIDs, Other CYP3A4 inhibitors).";
        }
    }
    return (
    
    <div style={{marginBottom: '20px', padding: '15px', background: '#1e272e', borderRadius: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button 
                type="button"
                className="no-print" 
                onClick={() => {
                    const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                    const trackingLabel = `${actualUserEmail}-printed-cyclosporin`;
                    logUserActivityQuietly(trackingLabel); 
                    window.print();
                }}
                style={{
                padding: '10px 20px',
                backgroundColor: '#27ae60', 
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                }}
            >
                🖨️ Print PDF Report
            </button>
        </div>
        <h2 style={{ textAlign: 'center', color: '#3498db', marginTop: 0 }}>Cyclosporin TDM Worksheet</h2>
        <details style={{
            backgroundColor: '#1E293B',
            border: '1px solid #38BDF8',
            borderRadius: '8px',
            padding: '10px 15px',
            marginBottom: '20px',
            cursor: 'pointer'
          }}>
            <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
              ⏱️ Check Clinical Sampling Protocol (Click to expand)
            </summary>
            <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
    
                {/* SECTION 1: SAMPLING MECHANICS & PRE-ANALYTICAL TUBE GUARD */}
                <div style={{ marginBottom: '15px' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                        ⏱️ Kinetic Timeline & Sampling Protocols
                    </span>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#fff', width: '35%' }}>Steady State:</td>
                                <td style={{ padding: '6px 0', color: '#bdc3c7' }}>Draw steady-state blood levels after **3 – 5 days** of initiation or dose adjustment.</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#fff' }}>Common Diagnosis:</td>
                                <td style={{ padding: '6px 0', color: '#bdc3c7' }}>Standard maintenance relies strictly on **Trough concentration ($C_0$)** drawn just before next dose.</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#3498db' }}>Acute Rejection:</td>
                                <td style={{ padding: '6px 0', color: '#fff' }}>
                                    ⚡ **Draw level at Post 2 Hours ($C_2$)** exactly following dose administration for critical exposure tracking.
                                </td>
                            </tr>
                            <tr>
                                <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#e74c3c' }}>Tube Requirement:</td>
                                <td style={{ padding: '6px 0', color: '#fff', fontWeight: 'bold' }}>
                                    💜 **Use EDTA tube** (Whole blood matrix is mandatory; serum separation clogs sample validity).
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* SECTION 2: THERAPEUTIC INDICATIONS INDEX MATRIX */}
                <div style={{ marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#2ecc71', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                        🎯 Non-Transplant Therapeutic Range References
                    </span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.85rem' }}>
                        {/* Column A Indications */}
                        <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div>🔹 **Nephrotic Syndrome:** <span style={{color: '#fff', fontWeight: 'bold'}}>125 - 250 mcg/L</span></div>
                            <div>🔹 **Lupus Nephritis:** <span style={{color: '#fff', fontWeight: 'bold'}}>75 - 200 mcg/L</span></div>
                            <div>🔹 **Aplastic Anemia:** <span style={{color: '#fff', fontWeight: 'bold'}}>75 - 200 mcg/L</span></div>
                        </div>
                        
                        {/* Column B Indications */}
                        <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div>🔹 **Ulcerative Colitis:** <span style={{color: '#fff', fontWeight: 'bold'}}>150 - 350 mcg/L</span></div>
                            <div>🔹 **Myasthenia Gravis (MG):** <span style={{color: '#fff', fontWeight: 'bold'}}>300 - 500 mcg/L</span></div>
                            <div>🌱 **Organ Transplants:** <span style={{color: '#95a5a6', fontStyle: 'italic'}}>Refer to specific indication target range as below</span></div>
                        </div>
                    </div>
                </div>

            </div>
        </details>
        <div style={{ padding: '25px', background: '#1b1f23', border: '2px solid #3498db', borderRadius: '12px' }}>
            <h4 style={{ color: '#2980b9', marginTop: 0}}>
                Step 1: Population-Based Clearance
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                {/* Age Input */}
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Age (Years):</label>
                    <input 
                        type="number" 
                        value={age} 
                        onChange={(e) =>{const val = e.target.value; setAge(val === "" ? "" : Number(val))}} 
                        style={inputStyle} 
                    />
                </div>
                {/* Weight Input */}
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Weight (kg):</label>
                    <input 
                        type="number" 
                        value={weight} 
                        onChange={(e) => {const val = e.target.value; setWeight(val === "" ? "" : Number(val))}} 
                        style={inputStyle} 
                    />
                </div>
                {/* Route Selection */}
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Route:</label>
                    <div style={{ marginTop: '5px' }}>
                        <button onClick={() => setRoute('oral')} style={btnStyle(route === 'oral')}>Oral</button>
                        <button onClick={() => setRoute('iv')} style={btnStyle(route === 'iv')}>IV</button>
                    </div>
                </div>
                {/* Liver Toggle */}
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Liver Function:</label>
                    <button 
                        onClick={() => setIsLiverFailure(!isLiverFailure)} 
                        style={btnStyle(isLiverFailure)}
                    >
                        {isLiverFailure ? "Liver Failure" : "Normal Liver"}
                    </button>
                </div>
            </div>
            {/* Transparency Box showing the 0.06 math */}
            <div style={{ background: '#314355', padding: '10px', borderRadius: '5px', fontSize: '0.85rem' }}>
                <strong>Formula:</strong> 
                <span style={{ color: '#d35400', fontWeight: 'bold' }}>{currentFactor}</span> 
                <span style={{ fontSize: '0.75rem', color: '#7f8c8d', marginLeft: '5px' }}>{factorLabel}</span>
                {""} ml/kg/min × {weight} kg × 0.06 = <strong>{clPopulation.toFixed(2)} L/hr</strong>
            </div>
        </div>
        <div style={{ padding: '18px', background: '#1b1f23', border: '2px solid #1abc9c', borderRadius: '12px', marginTop: '20px' }}>
            <h3 style={{ color: '#16a085', marginTop: 0 }}>Step 2: Clinical Indication & Target C₀</h3>
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    Monitoring Type:
                </label>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                    <button 
                        onClick={() => setLevelType('C0')} 
                        style={btnStyle(levelType === 'C0')}
                    >
                        C₀ (Trough)
                    </button>
                    <button 
                        onClick={() => setLevelType('C2')} 
                        style={btnStyle(levelType === 'C2')}
                    >
                        C₂ (2-hr Peak)
                    </button>
                </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold' }}>Indication:</label>
                <select 
                    value={indication} 
                    onChange={(e) => setIndication(e.target.value)}
                    style={inputStyle}
                >
                    {Object.keys(indicationTargets)
                        .filter(ind => {
                            // If mode is C2, only show Kidney and Liver
                            if (levelType === 'C2') {
                                return ind === "Kidney Transplant" || ind === "Liver Transplant";
                            }
                            // If mode is C0, show everything
                            return true;
                        })
                        .map(filteredInd => (
                            <option key={filteredInd} value={filteredInd}>
                                {filteredInd}
                            </option>
                        ))
                    }
                </select>
                {indication === "Kidney Transplant" && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                        
                        {/* LEFT COLUMN: MONTHS POST-TRANSPLANT SELECTOR */}
                        <div>
                            <label style={{ fontSize: '0.8rem' }}>Months Post-Transplant:</label>
                            <div style={{ marginTop: '5px' }}>
                                {levelType === 'C2' ? (
                                    /* 1. IF C2 PATHWAY IS ACTIVE (Adult Kidney Track) */
                                    ["0-1", "2", "3", "4-6", "7-12", ">12"].map(m => (
                                        <button key={m} onClick={() => setMonthsPost(m)} style={btnStyle(monthsPost === m)}>{m}m</button>
                                    ))
                                ) : (
                                    /* 2. IF C0 TROUGH PATHWAY IS ACTIVE (Differentiates Adult vs Pediatric) */
                                    parseFloat(age) >= 12 ? (
                                        /* Standard Adult C0 Windows */
                                        ["0-6", "7-12", ">12"].map(m => (
                                            <button key={m} onClick={() => setMonthsPost(m)} style={btnStyle(monthsPost === m)}>{m}</button>
                                        ))
                                    ) : (
                                        /* New Pediatric C0 Windows from tdm5.png */
                                        ["<3 months", ">3 months"].map(m => (
                                            <button key={m} onClick={() => setMonthsPost(m)} style={btnStyle(monthsPost === m)}>
                                                {m === '<3 months' ? '< 3m' : '> 3m'}
                                            </button>
                                        ))
                                    )
                                )}
                            </div>
                        </div>

                        {/* RIGHT COLUMN: REJECTION RISK MATRIX SELECTION (Adult Only) */}
                        {parseFloat(age) >= 12 && levelType === 'C0' && (
                            <div>
                                <label style={{ fontSize: '0.8rem' }}>Rejection Risk:</label>
                                <div style={{ marginTop: '5px' }}>
                                    {["Low", "Moderate", "High"].map(r => (
                                        <button key={r} onClick={() => setRisk(r)} style={btnStyle(risk === r)}>{r}</button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                    </div>
                )}
                {/* Sub-class for Liver Transplant (Pediatric Only) */}
                {indication === "Liver Transplant" && age < 12 && (
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Liver Pediatric Months:</label>
                        <div style={{ marginTop: '0px' }}>
                        {/* If C2 is selected, show C2-specific month buttons */}
                        {levelType === 'C2' ? (
                            ['0-3m','4-6m','>6'].map(m => (
                                <button key={m} onClick={() => setLiverMonths(m)} style={btnStyle(liverMonths === m)}>{m}</button>
                            ))
                        ) : (
                            ["<3 months", ">3 months"].map(m => (
                                <button key={m} onClick={() => setLiverMonths(m)} style={btnStyle(liverMonths === m)}>{m}</button>
                            ))
                        )}
                        </div>
                    </div>
                )}
                {/* Visual Hint for Heart/Lungs (Age Dependent) */}
                {indication === "Others transplant (heart, lungs)" && (
                    <div style={{ marginBottom: '15px', fontSize: '0.8rem', color: '#7f8c8d', fontStyle: 'italic' }}>
                        *Target automatically adjusted for {age < 12 ? "Pediatric" : "Adult"} indication.
                    </div>
                )}
                <div style={{ background: '#e8f8f5', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '1px solid #1abc9c' }}>
                    <span style={{ fontSize: '0.85rem', color: '#16a085' }}>Recommended {levelType} Target:</span>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#1abc9c' }}>
                        {getTargetRange()} <small style={{ fontSize: '0.9rem' }}>mcg/L</small>
                    </div>
                </div>
            </div>
        </div>
        <div style={{ padding: '25px', background: '#1b1f23', border: '2px solid #e67e22', borderRadius: '12px', marginTop: '20px' }}>
            <h3 style={{ color: '#e67e22', marginTop: 0 }}>Step 3: Dosage Prediction & Suitability</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Current Dose (mg/dose):</label>
                    <input 
                        type="number" 
                        value={currentDose} 
                        onChange={(e) => setCurrentDose(e.target.value)} 
                        placeholder="Enter mg per dose"
                        style={inputStyle} 
                    />
                </div>
                <div>
                    <label style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>Interval (hrly):</label>
                    <input 
                        type="number" 
                        value={interval} 
                        onChange={(e) => setInterval(e.target.value)} 
                        placeholder="12 hourly"
                        style={inputStyle} 
                    />
                </div>
                <div style={{ padding: '8px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.75rem', color: '#95a5a6' }}>PREDICTED LEVEL (EST.)</span>
                    <div style={{ fontSize: '1.0rem', fontWeight: 'bold', color: status.color }}>
                        {predictedCss > 0 ? Math.round(predictedCss) : '0'} <small style={{ fontSize: '0.9rem' }}>mcg/L</small>
                    </div>
                    
                    {/* Suitability Badge */}
                    <div style={{ 
                        marginTop: '5px', 
                        fontSize: '0.75rem', 
                        fontWeight: 'bold', 
                        color: '#fff', 
                        background: status.color, 
                        display: 'inline-block', 
                        padding: '2px 10px', 
                        borderRadius: '10px' 
                    }}>
                        {status.label}
                    </div>
                </div>
            </div>
            {/* Formula working for transparency */}
            <div style={{ background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '5px', fontSize: '0.8rem', color: '#bdc3c7' }}>
                <strong>Math:</strong> ({currentDose}mg × 0.3 × 1 × 1000) / ({clPopulation.toFixed(2)} L/hr × 12hr) = {predictedCss.toFixed(0)} mcg/L
            </div>
        </div>
        <section style={{ marginTop: '20px', padding: '20px', background: '#1b1f23', borderRadius: '12px', border: '1px solid #9b59b6' }}>
            <h4 style={{ color: '#9b59b6', marginTop: 0 }}>Step 4: Clinical Correlation (Reality vs. Prediction)</h4>
            
            <div style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Patient's Lab Measured Level (mcg/L):</label>
                <input 
                    type="number" 
                    value={measuredLevel} 
                    onChange={(e) => setMeasuredLevel(e.target.value)} 
                    placeholder="Enter actual lab result"
                    style={inputStyle} 
                />
            </div>

            {actualLevel > 0 && 
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>Lab Measured Level</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>
                            {measuredLevel} <small style={{ fontSize: '0.7rem' }}>mcg/L</small>
                        </div>
                    </div>

                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>INDIVIDUAL CL VS <small style = {{frontSize: '0.9rem',color: '#33d930'}}>POPULATION CL</small>  </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#fff' }}>
                            {individualCL.toFixed(2)} <small style={{ fontSize: '0.7rem' }}>L/hr</small> VS <small style = {{color: '#33d930'}}>{clPopulation.toFixed(2)} L/hr</small>
                        </div>
                    </div>

                    <div style={{ gridColumn: 'span 2', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', borderLeft: `4px solid ${metabolism.color}` }}>
                        <div style={{ fontWeight: 'bold', color: metabolism.color, fontSize: '0.9rem' }}>{metabolism.text}</div>
                        <div style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>{metabolism.note}</div>
                    </div>
                </div>
            }
        </section>
        {/* STEP 5: DOSE TITRATION SIMULATOR (INDIVIDUAL PATIENT HIGH-PRECISION MODEL) */}
        <section style={{ 
            marginTop: '25px', 
            padding: '20px', 
            background: '#1b1f23', 
            borderRadius: '12px', 
            border: '2px solid #2ecc71' 
        }}>
            <h4 style={{ color: '#2ecc71', margin: '0 0 15px 0', fontSize: '1.1rem' }}>Step 5: Dose Titration Simulator</h4>
            
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '15px' }}>
                <div style={{ flex: 1 }}>
                    <label style={{ fontSize: '0.8rem', color: '#bdc3c7', display: 'block', marginBottom: '5px' }}>Proposed New Dose (mg BD):</label>
                    <input 
                        type="number" 
                        step="25"
                        value={simDose}
                        onChange={(e) => setSimDose(e.target.value)}
                        placeholder="e.g. 125"
                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                        disabled={!measuredLevel || !currentDose}
                    />
                    <p style={{ fontSize: '0.7rem', color: '#7f8c8d', marginTop: '5px', margin: '5px 0 0 0' }}>
                        *Try adjusted titration in 25mg - 50mg steps for patient safety thresholds.
                    </p>
                </div>
                
                <div style={{ flex: 1, background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '8px', textAlign: 'center', border: '1px solid #2c3539' }}>
                    <div style={{ fontSize: '0.75rem', color: '#95a5a6' }}>ESTIMATED NEW LEVEL ({levelType})</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#00d2ff', margin: '4px 0' }}>
                        {Math.round(simResult)} <small style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#bdc3c7' }}>mcg/L</small>
                    </div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: simStatus.color, marginTop: '5px', textTransform: 'uppercase' }}>
                        {simStatus.label || 'Awaiting New Dose Input'}
                    </div>
                </div>
            </div>

            {/* VISUAL SPECTRUM PROGRESS SLIDER TRACK BAR */}
            {simResult > 0 && (() => {
                const rangeStr = getTargetRange();
                let minTarget = 100;
                let maxTarget = 300; // Default visualization baseline markers
                
                if (rangeStr.includes("-")) {
                    const parts = rangeStr.split("-").map(s => parseFloat(s.trim()));
                    minTarget = parts[0];
                    maxTarget = parts[1];
                } else if (!isNaN(parseFloat(rangeStr))) {
                    const targetVal = parseFloat(rangeStr);
                    minTarget = targetVal * 0.9;
                    maxTarget = targetVal * 1.1;
                }

                // Create a standard maximum chart landscape scale based on targets (e.g., max target * 1.5)
                const maxChartScale = Math.max(500, maxTarget * 1.5);
                const leftPercent = (minTarget / maxChartScale) * 100;
                const widthPercent = ((maxTarget - minTarget) / maxChartScale) * 100;
                const pinPercent = (simResult / maxChartScale) * 100;

                return (
                    <div style={{ padding: '15px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginTop: '15px' }}>
                        <div style={{ position: 'relative', height: '14px', background: '#2c3e50', borderRadius: '7px', margin: '15px 0 15px 0', overflow: 'visible' }}>
                            
                            {/* TARGET REGION SHADOW HIGHLIGHT */}
                            <div style={{ 
                                position: 'absolute', 
                                left: `${Math.max(0, Math.min(90, leftPercent))}%`, 
                                width: `${Math.max(10, Math.min(100, widthPercent))}%`, 
                                height: '100%', 
                                background: 'rgba(46, 204, 113, 0.22)', 
                                borderLeft: '1px dashed #2ecc71', 
                                borderRight: '1px dashed #2ecc71',
                                borderRadius: '4px'
                            }} />

                            {/* ACTIVE PINPOINT INDICATOR NODE */}
                            <div style={{
                                position: 'absolute',
                                left: `${Math.max(0, Math.min(98, pinPercent))}%`,
                                transform: 'translateX(-50%)',
                                width: '18px',
                                height: '18px',
                                top: '-2px',
                                background: '#f1c40f',
                                borderRadius: '50%',
                                boxShadow: '0 0 12px #f1c40f',
                                border: '2px solid #fff',
                                transition: 'left 0.2s ease-out',
                                zIndex: 10
                            }} />
                        </div>

                        {/* GRAPH SCALE FOOTER LABELS */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '12px' }}>
                            <span>0 mcg/L</span>
                            <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Target Spec: {rangeStr} mcg/L</span>
                            <span>{Math.round(maxChartScale)} mcg/L</span>
                        </div>

                        {/* CLEAR AUDIT PROOF LINEAR TRAIL TRACE */}
                        <div style={{ fontSize: '0.68rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.4', textAlign: 'left' }}>
                            {measuredVal > 0 ? (
                                <>
                                    Formula Predict Css: (Proposed New Dose / Current Daily Dose) × Actual Measured Lab Level<br />
                                    Individual Patient Linear Ratio Trace: ({simDoseNum} mg / {currentDoseNum} mg) × {measuredLevel} mcg/L = {Math.round(simResult)} mcg/L
                                </>
                            ) : (
                                <>
                                    Formula Predict Css: (Dose × F × S) / (Clearance Value × Dosing Interval)<br />
                                    Population Estimate Reference Trace: ({simDoseNum}mg × 0.3 × 1.0 × 1000) / ({clPopulation.toFixed(2)} L/hr × {interval || 12}hr)
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}
        </section>

        {measuredVal > 0 &&
            <section style={{ 
                marginTop: '25px', 
                padding: '20px', 
                background: '#1b1f23', 
                borderRadius: '12px', 
                borderLeft: `8px solid ${measuredStatus.color}`,
                boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
            }}>
                <h4 style={{ color: measuredStatus.color, margin: '0 0 10px 0', textTransform: 'uppercase', fontSize: '0.95rem', letterSpacing: '0.5px' }}>
                    Step 6: Interpretation & Recommendation
                </h4>

                <div style={{ marginBottom: '15px' }}>
                    <div style={{ color: '#95a5a6', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>Assessment:</div>
                    <div style={{ color: '#ecf0f1', fontSize: '0.95rem', lineHeight: '1.5', marginTop: '3px' }}>
                        <strong>{measuredStatus.label}:</strong> {interpretation}
                    </div>
                </div>

                <div style={{ padding: '15px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)' }}>
                    <div style={{ color: '#2ecc71', fontSize: '0.75rem', fontWeight: 'bold', textTransform: 'uppercase' }}>Proposed Plan:</div>
                    <div style={{ color: '#fff', fontSize: '0.95rem', marginTop: '5px', lineHeight: '1.5' }}>
                        {recommendation} {simDoseNum > 0 && `Your simulated trial dose of ${simDoseNum} mg BD is projected to deliver a steady-state value of approximately ${Math.round(simResult)} mcg/L.`}
                    </div>
                </div>

                {/* Clinical Reminder */}
                <div style={{ marginTop: '15px', fontSize: '0.7rem', color: '#7f8c8d', fontStyle: 'italic' }}>
                    *Note: Always correlate with Serum Creatinine trajectories, individual graft function markers, and definitive clinical bedside indicators of toxicity or active rejection.
                </div>
            </section>
        }

        {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
        <p style={{ margin: '20px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
            *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
        </p>
    </div>

    );

}
const btnStyle = (active) => ({
    padding: '8px 15px',
    cursor: 'pointer',
    backgroundColor: active ? '#3498db' : '#ecf0f1',
    color: active ? '#ffffff' : '#2c3e50',
    border: '1px solid #bdc3c7',
    borderRadius: '4px',
    marginRight: '5px',
    fontSize: '0.8rem',
    fontWeight: 'bold'
});
const inputStyle = {
    width: '80%',
    padding: '8px',
    marginTop: '5px',
    borderRadius: '4px',
    border: '1px solid #ccc'
};
export default CyclosporinCalculator;